import {
  OfferStatus,
  OfferTransactionType,
  type CreateOfferReceiptDto,
  type RefUserDto,
  type ResponseOfferDto,
  type ResponseOfferReceiptDto,
} from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { type ReadonlyRefOrGetter } from '@vueuse/core';
import { defineStore } from 'pinia';
import type { Ref } from 'vue';
import { computed, onUnmounted, ref, toValue, watch } from 'vue';
import { useRouter } from 'vue-router';
import { isPolledOfferStatus, useOffer } from '../composables/offer/useOffer';
import { usePiniaRefStore } from '../composables/usePiniaRefStore';
import { useOfferItems } from '../composables/item/useOfferItems';
import { useAuth } from './authStore';

export interface NewOfferOptions {
  reference: string;
  image?: Blob;
  transition?: boolean;
}

export interface UseBasketOptions {
  takeLock?: boolean;
  onOfferChange?: (
    offer: ResponseOfferDto | null,
    previousOffer?: ResponseOfferDto | null,
  ) => void | Promise<void>;
  validOfferStatus?: OfferStatus[];
  redirectToReceipt?: boolean;
  redirectToProcessTransaction?: boolean;
  refetchInterval?: number | false;
}

export const useBasketStore = defineStore(
  'basket',
  () => {
    const offerId = ref<number>(-1);
    const takeLock = ref<boolean>(false);
    const selectedItemIds = ref<number[]>([]);
    const receiptData = ref<Partial<CreateOfferReceiptDto>>({});

    const selectedItemCount = computed(() => selectedItemIds.value.length);
    const addressCompleted = computed(
      () =>
        !!receiptData.value.firstName &&
        !!receiptData.value.lastName &&
        !!receiptData.value.address &&
        !!receiptData.value.city &&
        !!receiptData.value.postalCode &&
        !!receiptData.value.birthDate,
    );
    const transactionTypeCompleted = computed(
      () => receiptData.value.transactionType !== undefined,
    );

    const resetBasket = () => {
      receiptData.value = { country: 'DE' };
      selectedItemIds.value = [];
    };

    /**
     * Put the seller details of an existing receipt back into the basket so the
     * accept flow can be picked up again after a payout was taken back. The
     * payout method is deliberately dropped - that is what gets re-chosen.
     */
    const restoreReceipt = ({
      firstName,
      lastName,
      email,
      phone,
      birthDate,
      company,
      address,
      address2,
      postalCode,
      city,
      country,
    }: ResponseOfferReceiptDto) => {
      receiptData.value = {
        ...receiptData.value,
        firstName,
        lastName,
        email,
        phone,
        birthDate,
        company,
        address,
        address2,
        postalCode,
        city,
        country,
        transactionType: undefined,
      };
    };

    /** Wipe everything, including the offer reference and any customer PII. */
    const clearBasket = () => {
      offerId.value = -1;
      takeLock.value = false;
      resetBasket();
    };

    watch(offerId, (id, previousId) => {
      if (id > 0 && previousId > 0 && id !== previousId) resetBasket();
    });

    // A user swapping off a checked-in device keeps the in-progress basket for
    // the next PIN login (isPosMode true); a real sign-out must not leave the
    // customer's details behind in localStorage.
    useAuth({
      global: true,
      onLogout: (_userId, isPosMode) => {
        if (!isPosMode) clearBasket();
      },
    });

    return {
      offerId,
      takeLock,
      selectedItemIds,
      selectedItemCount,
      receiptData,
      addressCompleted,
      transactionTypeCompleted,
      resetBasket,
      restoreReceipt,
      clearBasket,
    };
  },
  {
    persist: { omit: ['takeLock'] },
  },
);

export const useBasket = (
  withOfferId?: ReadonlyRefOrGetter<number> | Ref<number> | null,
  {
    takeLock,
    validOfferStatus,
    redirectToReceipt,
    redirectToProcessTransaction,
    onOfferChange,
    refetchInterval = false,
  }: UseBasketOptions = {},
) => {
  const router = useRouter();
  const store = usePiniaRefStore(useBasketStore());

  const offer = useOffer(store.offerId, { refetchInterval, tryTakeLock: store.takeLock });
  const items = useOfferItems(store.offerId, {
    refetchOffer: offer.refetchOffer,
    // A parked or finished offer has no item changes to pick up either.
    refetchInterval: () =>
      isPolledOfferStatus(offer.offer.value?.status) ? refetchInterval : false,
  });

  if (withOfferId) {
    watch(withOfferId, (newOfferId) => (store.offerId.value = newOfferId), { immediate: true });
    store.takeLock.value = !!takeLock;
  }

  if (takeLock) {
    let keepLockTimeout: ReturnType<typeof setTimeout> | null = null;
    const renewLock = (lockedBy: RefUserDto | undefined) => {
      if (keepLockTimeout) {
        clearTimeout(keepLockTimeout);
        keepLockTimeout = null;
      }

      if (!lockedBy || offer.userHasLock.value) offer.acquireLock({});

      keepLockTimeout = setTimeout(() => renewLock(offer.lockedBy.value), 60000);
    };

    const unwatchRenewLock = watch(offer.lockedBy, renewLock);

    const releaseLock = () => {
      if (store.offerId.value >= 0 && offer.userHasLock.value) offer.releaseLock();

      if (keepLockTimeout) clearTimeout(keepLockTimeout);
      window.removeEventListener('beforeunload', releaseLock);
      unwatchRenewLock();
      store.takeLock.value = false;
    };

    onUnmounted(releaseLock);
    window.addEventListener('beforeunload', releaseLock);
  }

  if (redirectToReceipt || redirectToProcessTransaction || validOfferStatus?.length) {
    const originalOfferChange = onOfferChange;
    onOfferChange = async (newOffer, previousOffer) => {
      if (originalOfferChange) await originalOfferChange(newOffer, previousOffer);

      if (newOffer && redirectToReceipt && newOffer?.status === OfferStatus.TRANSACTION_COMPLETE) {
        router.replace({ name: 'offerReceipt', params: { offerId: newOffer.id } });
      } else if (
        newOffer &&
        redirectToProcessTransaction &&
        newOffer?.status === OfferStatus.AWAITING_TRANSACTION
      ) {
        router.replace({
          name:
            newOffer?.activeReceipt?.transactionType === OfferTransactionType.GIFT_CARD
              ? 'offerTransactionProcessGiftCard'
              : newOffer?.activeReceipt?.transactionType === OfferTransactionType.SHOPIFY_CASH
                ? 'offerTransactionProcessCash'
                : newOffer?.activeReceipt?.transactionType === OfferTransactionType.EXTERNAL_PAYMENT
                  ? 'offerTransactionProcessExternalPayment'
                  : newOffer?.activeReceipt?.transactionType === OfferTransactionType.PAYPAL_PAYOUT
                    ? 'offerTransactionProcessPayPal'
                    : newOffer?.activeReceipt?.transactionType ===
                        OfferTransactionType.INTERNAL_LEDGER_CASH
                      ? 'offerTransactionProcessInternalLedgerCash'
                      : // TODO: Add incompatible transaction type page
                        'offerTransactionProcessCash',
          params: { offerId: newOffer.id },
        });
      } else if (
        newOffer &&
        validOfferStatus?.length &&
        !validOfferStatus?.includes(newOffer.status)
      ) {
        router.replace({ name: 'offerInvalidStatus', params: { offerId: newOffer.id } });
      }
    };
  }

  if (onOfferChange) {
    watch(
      [offer.offer, offer.offerLoading],
      ([newOffer, isLoading], [previousOffer]) => {
        const offerId = toValue(withOfferId) ?? store.offerId.value;
        if (!isLoading && newOffer?.id === offerId) onOfferChange(newOffer, previousOffer);
      },
      { immediate: true },
    );
  }

  watch(offer.offer, async (newOffer, originalOffer) => {
    if (newOffer && originalOffer && newOffer?.needsReview !== originalOffer?.needsReview) {
      await items.refetchOfferItems();
    }
  });

  watch(items.includedItems, (includedItems) => {
    if (includedItems.length > 0) {
      store.selectedItemIds.value = includedItems.map((item) => item.id);
    }
  });

  const isPolling = computed(() => offer.offerPolling.value || items.itemsPolling.value);

  return { ...store, ...offer, ...items, isPolling };
};
